import { GlightBox } from '@/components';
import { useToggle } from '@/hooks';
import { Alert, Button, Card, Col, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalHeader, Row } from 'react-bootstrap';
import { BsExclamationOctagonFill, BsEyeFill, BsFullscreen, BsGeoAlt, BsPinMapFill, BsXLg } from 'react-icons/bs';
import { FaFacebookSquare, FaShareAlt, FaTwitterSquare } from 'react-icons/fa';
import { FaCopy, FaHeart, FaLinkedin } from 'react-icons/fa6';
import { Link } from '@/compat/react-router-dom';
import { useMemo } from 'react';
import { getHotelImage, toImageSrc } from '@/utils/imageUrl';

const HotelGallery = ({ hotel }) => {
  const { isOpen, toggle } = useToggle();
  const { isOpen: alertVisible, hide: hideAlert } = useToggle(false);

  // Get hotel images from database and media folder
  const galleryImages = useMemo(() => {
    if (!hotel) return [];
    
    const images = [];
    
    // Check for images in various locations
    // 1. Check hotel.images array
    if (hotel.images && Array.isArray(hotel.images)) {
      images.push(...hotel.images);
    }
    
    // 2. Check hotel.image (single image)
    if (hotel.image) {
      images.push(hotel.image);
    }
    
    // 3. Check hotel.raw?.images
    if (hotel.raw?.images && Array.isArray(hotel.raw.images)) {
      images.push(...hotel.raw.images);
    }
    
    // 4. Check hotel.webhotelier photos (cached)
    if (hotel.webhotelier?.photos && Array.isArray(hotel.webhotelier.photos)) {
      hotel.webhotelier.photos.forEach(photo => {
        if (photo?.url) {
          images.push(photo.url);
        }
      });
    }
    
    // 5. Check hotel.media array (if backend returns media objects)
    if (hotel.media && Array.isArray(hotel.media)) {
      hotel.media.forEach(mediaItem => {
        if (mediaItem.file_url) {
          images.push(mediaItem.file_url);
        } else if (mediaItem.url) {
          images.push(mediaItem.url);
        } else if (mediaItem.image_path) {
          images.push(mediaItem.image_path);
        } else if (typeof mediaItem === 'string') {
          images.push(mediaItem);
        }
      });
    }
    
    // 6. Check hotel.gallery (if backend has a gallery field)
    if (hotel.gallery && Array.isArray(hotel.gallery)) {
      images.push(...hotel.gallery);
    }
    
    // Remove duplicates and filter out null/undefined
    const uniqueImages = [...new Set(
      images
        .map((img) => toImageSrc(img, null))
        .filter(Boolean)
    )];
    
    // If no images, return empty array (will show placeholder)
    return uniqueImages.slice(0, 10); // Allow more images for gallery
  }, [hotel]);

  const hotelName = hotel?.name || 'Hotel';
  const location = hotel?.area?.name || 
                   hotel?.region_name || 
                   hotel?.destination_name || 
                   hotel?.location || 
                   'Crete';

  return (
    <>
      <section className="py-0 pt-sm-5">
        <Container className="position-relative">
          <Row className="mb-3">
            <Col xs={12}>
              <div className="d-lg-flex justify-content-lg-between mb-1">
                <div className="mb-2 mb-lg-0">
                  <h1 className="fs-2">{hotelName}</h1>
                  <p className="fw-bold mb-0 items-center flex-wrap">
                    <BsGeoAlt className="me-2" />
                    {location}
                    <Link to="" onClick={toggle} className="ms-3 text-decoration-underline items-center" data-bs-toggle="modal" data-bs-target="#mapmodal">
                      <BsEyeFill className="me-1" />
                      View On Map
                    </Link>
                  </p>
                </div>
                <ul className="list-inline text-end">
                  <li className="list-inline-item">
                    <Button variant="light" size="sm" className="px-2">
                      <FaHeart className="fa-fw" />
                    </Button>
                  </li>
                  <Dropdown className="list-inline-item dropdown">
                    <DropdownToggle className="btn btn-sm btn-light px-2 arrow-none" role="button" id="dropdownShare" data-bs-toggle="dropdown" aria-expanded="false">
                      <FaShareAlt className="fa-fw" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end min-w-auto shadow rounded" aria-labelledby="dropdownShare">
                      <DropdownItem href="">
                        <FaTwitterSquare className="me-2" />
                        Twitter
                      </DropdownItem>
                      <DropdownItem href="">
                        <FaFacebookSquare className="me-2" />
                        Facebook
                      </DropdownItem>
                      <DropdownItem href="">
                        <FaLinkedin className="me-2" />
                        LinkedIn
                      </DropdownItem>
                      <DropdownItem href="">
                        <FaCopy className="me-2" />
                        Copy link
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </ul>
              </div>
            </Col>
          </Row>
          {alertVisible && (
            <Alert show={alertVisible} variant="danger" className="d-flex justify-content-between align-items-center rounded-3 fade show mb-4 mb-0 pe-2 py-3" role="alert">
              <div className="items-center">
                <span className="alert-heading h5 mb-0 me-2">
                  <BsExclamationOctagonFill />
                </span>
                <span>
                  <strong className="alert-heading me-2">Important:</strong>Please check hotel policies before booking
                </span>
              </div>
              <Button variant="link" onClick={hideAlert} type="button" className="pb-0 pt-1 text-end" data-bs-dismiss="alert" aria-label="Close">
                <BsXLg className="text-dark" />
              </Button>
            </Alert>
          )}
        </Container>
      </section>

      <section className="card-grid pt-0">
        <Container>
          <Row className="g-2">
            {galleryImages.length > 0 ? (
              <>
                <Col md={6}>
                  <GlightBox data-glightbox data-gallery="hotel-gallery" image={getHotelImage(galleryImages[0], null)}>
                    <Card className="card-grid-lg card-element-hover card-overlay-hover overflow-hidden" style={{
                      backgroundImage: `url(${getHotelImage(galleryImages[0], null)})`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover'
                    }}>
                      <div className="hover-element position-absolute w-100 h-100">
                        <BsFullscreen size={28} className="fs-6 text-white position-absolute top-50 start-50 translate-middle bg-dark rounded-1 p-2 lh-1" />
                      </div>
                    </Card>
                  </GlightBox>
                </Col>
                <Col md={6}>
                  <Row className="g-2">
                    {galleryImages[1] && (
                      <Col xs={12}>
                        <GlightBox data-glightbox data-gallery="hotel-gallery" image={getHotelImage(galleryImages[1], null)}>
                          <Card className="card-grid-sm card-element-hover card-overlay-hover overflow-hidden" style={{
                            backgroundImage: `url(${getHotelImage(galleryImages[1], null)})`,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover'
                          }}>
                            <div className="hover-element position-absolute w-100 h-100">
                              <BsFullscreen size={28} className="fs-6 text-white position-absolute top-50 start-50 translate-middle bg-dark rounded-1 p-2 lh-1" />
                            </div>
                          </Card>
                        </GlightBox>
                      </Col>
                    )}
                    {galleryImages[2] && (
                      <Col md={6}>
                        <GlightBox data-glightbox data-gallery="hotel-gallery" image={getHotelImage(galleryImages[2], null)}>
                          <Card className="card-grid-sm card-element-hover card-overlay-hover overflow-hidden" style={{
                            backgroundImage: `url(${getHotelImage(galleryImages[2], null)})`,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover'
                          }}>
                            <div className="hover-element position-absolute w-100 h-100">
                              <BsFullscreen size={28} className="fs-6 text-white position-absolute top-50 start-50 translate-middle bg-dark rounded-1 p-2 lh-1" />
                            </div>
                          </Card>
                        </GlightBox>
                      </Col>
                    )}
                    {galleryImages[3] && (
                      <Col md={6}>
                        <GlightBox data-glightbox data-gallery="hotel-gallery" image={getHotelImage(galleryImages[3], null)}>
                          <Card className="card-grid-sm card-element-hover card-overlay-hover overflow-hidden position-relative" style={{
                            backgroundImage: `url(${getHotelImage(galleryImages[3], null)})`,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover'
                          }}>
                            <div className="bg-overlay bg-dark opacity-7" />
                            {galleryImages.length > 4 && (
                              <div className="card-img-overlay d-flex h-100 w-100">
                                <h6 className="card-title m-auto fw-light text-decoration-underline">
                                  <span className="text-white">
                                    View all {galleryImages.length} photos
                                  </span>
                                </h6>
                              </div>
                            )}
                            <div className="hover-element position-absolute w-100 h-100">
                              <BsFullscreen size={28} className="fs-6 text-white position-absolute top-50 start-50 translate-middle bg-dark rounded-1 p-2 lh-1" />
                            </div>
                          </Card>
                        </GlightBox>
                      </Col>
                    )}
                  </Row>
                </Col>
                {/* Hidden images for gallery - all images beyond the first 4 */}
                {galleryImages.slice(4).map((img, idx) => (
                  <GlightBox 
                    key={`hidden-${idx}`} 
                    data-glightbox 
                    data-gallery="hotel-gallery" 
                    image={getHotelImage(img, null)}
                    style={{ display: 'none' }}
                  />
                ))}
              </>
            ) : (
              <Col xs={12}>
                <Card className="card-grid-lg" style={{ minHeight: '400px', backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted">No images available</p>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </section>

      <Modal size="lg" centered show={isOpen} onHide={toggle} className="fade">
        <ModalHeader>
          <h5 className="modal-title" id="mapmodalLabel">
            View Our Hotel Location
          </h5>
          <button type="button" onClick={toggle} className="btn-close" />
        </ModalHeader>
        <div className="modal-body p-0">
          <iframe 
            className="w-100" 
            height={400} 
            src={`https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${encodeURIComponent(location)}`}
            style={{ border: 0 }} 
            title="map" 
            aria-hidden="false" 
            tabIndex={0} 
          />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm btn-primary mb-0 items-center">
            <BsPinMapFill className="me-2" />
            View In Google Map
          </button>
        </div>
      </Modal>
    </>
  );
};

export default HotelGallery;

